import React, { useEffect, useState } from "react";
import { useCompanion } from "@/companion/CompanionContext";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import axios from "axios";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { celebrateSigned } from "@/lib/celebrate";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicOffer() {
  const companion = useCompanion();
  const { token } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | signed | expired | error
  const [errMsg, setErrMsg] = useState("");
  const [typedName, setTypedName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${BASE_URL}/api/public/offer/${token}`)
      .then((r) => {
        setOffer(r.data);
        if (r.data.status === "signed") setStatus("signed");
        else setStatus("ready");
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || "This link is not valid.";
        setErrMsg(detail);
        setStatus(e?.response?.status === 410 ? "expired" : "error");
      });
  }, [token]);

  const submit = async () => {
    if (!accepted) return;
    if (!typedName.trim()) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${BASE_URL}/api/public/offer/${token}/accept`, {
        typed_name: typedName.trim(),
        accepted: true,
      });
      setStatus("signed");
      setOffer((o) => ({ ...o, signed_at: r.data.signed_at, signed_name: typedName.trim() }));
      // Gentle celebration — this is a big moment for a new hire.
      setTimeout(() => celebrateSigned(), 150);
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="public-offer">
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-baseline gap-3">
          <span className="text-2xl font-semibold tracking-tight text-stone-900">{companion.productWordmark}</span>
          <span className="text-[10px] font-medium uppercase text-stone-400 tracking-wider">Rainbow Interests · Offer of employment</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {status === "loading" && (
          <div className="text-center py-24 text-stone-400 text-sm flex items-center justify-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading offer…</div>
        )}

        {status === "expired" && (
          <StatusCard tone="warn" icon={AlertTriangle}
            title="This link has expired"
            body="Please reach out to Sheila Wilkinson to have a new signing link issued."
          />
        )}
        {status === "error" && (
          <StatusCard tone="warn" icon={AlertTriangle} title="We couldn't find this offer" body={errMsg} />
        )}

        {status === "signed" && offer && (
          <>
            <StatusCard tone="success" icon={CheckCircle2}
              title="Offer signed — welcome aboard!"
              body={`Signed by ${offer.signed_name || "you"} on ${(offer.signed_at || "").slice(0, 10)}. Sheila and your GM will be in touch about training and next steps.`}
            />
            <OfferBody body={offer.rendered_body} />
          </>
        )}

        {status === "ready" && offer && (
          <>
            <OfferBody body={offer.rendered_body} />
            <section className="mt-8 bg-white border border-stone-200 rounded-2xl p-6" data-testid="sign-panel">
              <div className="font-serif-display text-lg text-stone-900 mb-1">Acceptance of Offer</div>
              <p className="text-sm text-stone-600 mb-4">
                By typing your full name below and checking the box, you acknowledge that you understand and accept the terms of employment set forth in this letter.
              </p>
              <label className="block text-[10px] label-eyebrow text-stone-500 mb-1">Full legal name (must match "{offer.candidate_name}")</label>
              <input
                value={typedName}
                onChange={(e) => { setTypedName(e.target.value); if (errMsg) setErrMsg(""); }}
                data-testid="sign-name"
                placeholder={offer.candidate_name}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm outline-none focus:border-stone-500 mb-3"
              />
              <label className="flex items-start gap-2 text-sm text-stone-700 mb-4">
                <input type="checkbox" checked={accepted} onChange={(e) => { setAccepted(e.target.checked); if (errMsg) setErrMsg(""); }} data-testid="sign-checkbox" className="mt-1" />
                <span>I, {typedName || "________"}, hereby acknowledge that I understand and accept the terms of my employment as set forth above.</span>
              </label>
              {errMsg && <div className="text-xs text-rose-600 mb-3">{errMsg}</div>}
              <button
                onClick={submit}
                disabled={submitting || !accepted || !typedName.trim()}
                data-testid="sign-submit"
                className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-stone-900 text-white text-sm hover:bg-stone-800 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Signing…" : "Accept offer"}
              </button>
            </section>
          </>
        )}
      </main>
      <style>{`
        .offer-body h3 { font-family: var(--font-serif, Georgia, serif); font-size: 1.15rem; color: #1c1917; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .offer-body p { color: #44403c; font-size: 0.95rem; line-height: 1.7; margin: 0.75rem 0; }
        .offer-body strong { color: #1c1917; font-weight: 600; }
        .offer-body em { color: #57534e; }
        .offer-body ul, .offer-body ol { padding-left: 1.5rem; font-size: 0.95rem; color: #44403c; margin: 0.5rem 0; }
        .offer-body li { margin: 0.25rem 0; }
        .offer-body table { border-collapse: collapse; margin: 1rem 0; font-size: 0.85rem; width: 100%; }
        .offer-body th, .offer-body td { border: 1px solid #e7e5e4; padding: 0.5rem 0.75rem; text-align: left; }
        .offer-body th { background: #fafaf9; font-weight: 500; color: #292524; }
      `}</style>
    </div>
  );
}

function StatusCard({ tone, icon: Icon, title, body }) {
  const cls = tone === "success"
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : "bg-amber-50 border-amber-200 text-amber-800";
  return (
    <section className={`rounded-2xl border p-5 flex items-start gap-3 ${cls}`} data-testid={`status-${tone}`}>
      <Icon size={20} strokeWidth={1.5} className="shrink-0 mt-0.5" />
      <div>
        <div className="font-serif-display text-lg leading-tight">{title}</div>
        <div className="text-sm mt-1 opacity-90">{body}</div>
      </div>
    </section>
  );
}

function OfferBody({ body }) {
  return (
    <section className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-10 mt-6" data-testid="offer-body">
      <div className="offer-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{body || ""}</ReactMarkdown>
      </div>
    </section>
  );
}
